import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import InfoIcon from "../../assets/images/icons/InfoIcon";
import APIDocsLink from "../../components/common/APIDocsLink/APIDocsLink";
import BriaTooltip from "../../components/common/BriaTooltip/BriaTooltip";
import styles from "./InputLayout.module.scss";

interface IProps {
	label?: string | React.ReactNode;
	labelClassName?: string;
	labelIcon?: any;
	labelSize?: "large" | "extraLarge" | "title";
	info?: string;
	description?: string | React.ReactNode;
	descriptionPosition?: "top" | "bottom";
	docsLink?: string;
	className?: string;
	children: React.ReactNode;
	showLabel?: boolean;
	validateOn?: boolean;
	validationMessage?: string | null;
	optional?: boolean;
	optionalLabel?: string;
	apiDocLink?: string;
	actionButton?: React.ReactNode;
	id?: string;
}

const InputLayout = ({
	label,
	labelClassName,
	labelIcon,
	labelSize,
	info,
	description,
	descriptionPosition = "top",
	docsLink,
	showLabel = true,
	validateOn = false,
	validationMessage = "",
	className,
	children,
	optional = false,
	optionalLabel,
	apiDocLink,
	actionButton,
	id,
}: IProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "inputLayout" });

	return (
		<Box className={clsx(styles.container, className)} id={id}>
			<Box className={styles.labelContainer}>
				{showLabel && (
					<Box
						className={clsx(
							styles.flexLabel,
							{
								[styles.textLabel]: !optional,
							},
							labelClassName,
						)}
					>
						<Box className={styles.leftSide}>
							<Typography
								className={clsx(styles.inputLabel, {
									[styles.largeLabel]: labelSize === "large",
									[styles.extraLargeLabel]: labelSize === "extraLarge",
									[styles.titleLabel]: labelSize === "title",
								})}
							>
								{label || <span>&nbsp;</span>}
							</Typography>
							{apiDocLink && <APIDocsLink link={apiDocLink} />}
							{info && (
								<BriaTooltip
									title={info}
									className={styles.info}
									componentsProps={{ tooltip: { className: styles.tooltip } }}
									displayOn="click"
								>
									<InfoIcon className={styles.infoIcon} />
								</BriaTooltip>
							)}
							{docsLink && (
								<NavLink className={styles.docsLink} to={docsLink} target="_blank">
									{t("docsLink")}
								</NavLink>
							)}
							{labelIcon && <img className={styles.labelIcon} src={labelIcon} />}
						</Box>
						<Box className={styles.rightSide}>
							{actionButton && <Box className={styles.actionButton}>{actionButton}</Box>}
							{optional && (
								<Typography className={styles.optionalLabel}>
									{optionalLabel ?? t("optional")}
								</Typography>
							)}
						</Box>
					</Box>
				)}
				{description && descriptionPosition === "top" && (
					<Typography className={styles.description}>{description}</Typography>
				)}
			</Box>
			{children}
			{validateOn && <Typography className={styles.inputError}>{validationMessage}</Typography>}
			{description && descriptionPosition === "bottom" && (
				<Typography className={styles.description}>{description}</Typography>
			)}
		</Box>
	);
};

export default observer(InputLayout);
